import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  LogOut, Lock, Unlock, Loader2, AlertTriangle, CheckCircle, Clock,
  ClipboardCheck, FileText, ChevronRight, Calendar, X, Send, UserPlus, Mail,
} from 'lucide-react';
import ReportCharts from './portal/ReportCharts';
import BillingOnboardingModal from './portal/BillingOnboardingModal';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const CUSTOMER_TOKEN_KEY = 'enbet_customer_token';

const portalHeaders = () => {
  const t = localStorage.getItem(CUSTOMER_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// =============================================================================
// Domänen-Labels (Sessions)
// =============================================================================

const DOMAIN_LABELS = {
  prozess: 'Prozesse',
  controlling: 'Controlling',
  operations: 'Operations',
  marketing: 'Marketing',
  strategie: 'Strategie',
  hr: 'HR',
};

// Pro Domain ein KI-Experte mit Namen (3 weiblich / 3 männlich).
// Reihenfolge spiegelt die Termin-Reihenfolge (Gruppen-Termin 2-5).
const DOMAIN_EXPERT = {
  prozess:     { name: 'Max',    role: 'Prozess-Experte',     gender: 'm' },
  controlling: { name: 'Clara',  role: 'Controlling-Expertin', gender: 'w' },
  operations:  { name: 'Oskar',  role: 'Operations-Experte',  gender: 'm' },
  marketing:   { name: 'Mia',    role: 'Marketing-Expertin',  gender: 'w' },
  strategie:   { name: 'Stefan', role: 'Strategie-Experte',   gender: 'm' },
  hr:          { name: 'Hanna',  role: 'HR-Expertin',         gender: 'w' },
};
const DOMAIN_ORDER = ['prozess', 'controlling', 'operations', 'marketing', 'strategie', 'hr'];

const SESSION_STATUS = {
  locked: { label: 'Noch gesperrt', cls: 'bg-gray-100 text-gray-500', icon: Lock },
  unlocked: { label: 'Bereit zum Start', cls: 'bg-[#befa0a]/30 text-[#658a07]', icon: Unlock },
  in_progress: { label: 'Läuft', cls: 'bg-amber-100 text-amber-700', icon: Loader2 },
  awaiting_chef_approval: { label: 'Wartet auf Ihre Freigabe', cls: 'bg-blue-100 text-blue-700', icon: AlertTriangle },
  approved: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  skipped: { label: 'Übersprungen', cls: 'bg-gray-100 text-gray-400', icon: ChevronRight },
};

function formatGermanDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

// =============================================================================
// Hauptkomponente
// =============================================================================

export default function CustomerPortal() {
  const [authed, setAuthed] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const t = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/portal/me`, { headers: portalHeaders() });
      setCustomer(data);
      setAuthed(true);
    } catch {
      localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogin = (token, cust) => {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
    setCustomer(cust);
    setAuthed(true);
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/portal/auth/logout`, {}, { headers: portalHeaders() }); } catch {}
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setAuthed(false);
    setCustomer(null);
  };

  if (loading) return <div className="min-h-screen bg-[#fafafa] flex items-center justify-center"><Loader2 size={32} className="animate-spin text-gray-400" /></div>;
  if (!authed) return <LoginScreen onLogin={handleLogin} />;
  return <PortalDashboard customer={customer} onLogout={handleLogout} />;
}

// =============================================================================
// Login-Screen
// =============================================================================

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/portal/auth/login`, { email, password });
      onLogin(data.token, data.customer);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login fehlgeschlagen');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            EnBet Consulting · Kundenbereich
          </p>
          <h1 className="text-white text-3xl font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Ihr Portal
          </h1>
        </div>

        <form onSubmit={submit} className="bg-white p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
              <AlertTriangle size={14} className="inline mr-2" /> {error}
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Passwort</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>

          <button type="submit" disabled={busy}
            className="w-full bg-[#0a0a0a] text-[#befa0a] px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {busy ? <><Loader2 size={14} className="animate-spin" /> Login…</> : 'Einloggen'}
          </button>

          <p className="text-[11px] text-gray-400 text-center pt-2">
            Zugangsdaten nicht zur Hand? Schreiben Sie an <a href="mailto:hallo@enbet-consulting.de" className="text-[#658a07] hover:underline">hallo@enbet-consulting.de</a>
          </p>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Dashboard
// =============================================================================

function PortalDashboard({ customer: initialCustomer, onLogout }) {
  const [customer, setCustomer] = useState(initialCustomer);
  const [readyCheck, setReadyCheck] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [billingComplete, setBillingComplete] = useState(true);
  const [showBillingWizard, setShowBillingWizard] = useState(false);

  // Billing-Profil-Stand laden (Banner ggf. anzeigen)
  const loadBillingStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portal/me/billing-profile`, { headers: portalHeaders() });
      setBillingComplete(!!data.is_complete);
    } catch {
      // Profil-Endpoint kaputt → Banner sicherheitshalber zeigen
      setBillingComplete(false);
    }
  }, []);
  useEffect(() => { loadBillingStatus(); }, [loadBillingStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portal/readycheck`, { headers: portalHeaders() });
      setReadyCheck(data.ready_check);
      setMilestones(data.milestones || []);
      setSessions(data.sessions || []);
    } catch {
      // Kein Ready-Check vorhanden — normal, zeigt Welcome-Screen
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header className="bg-[#0a0a0a] text-white px-6 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              EnBet · Kundenbereich
            </p>
            <h1 className="text-lg md:text-xl font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {customer.company_name || customer.contact_name || customer.email}
            </h1>
          </div>
          <button onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/70 hover:text-[#befa0a]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <LogOut size={14} /> Abmelden
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 md:py-12">
        {!billingComplete && !customer?.must_change_password && (
          <div className="mb-6 bg-amber-50 border border-amber-300 p-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-900" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Bitte vervollständigen Sie Ihre Daten
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Damit wir saubere Rechnungen und Auftragsbestätigungen für Sie erstellen können, brauchen wir noch
                  Ihre Firmendaten und die Zustimmung zu unseren AGB. Dauert ca. 3 Minuten.
                </p>
              </div>
            </div>
            <button onClick={() => setShowBillingWizard(true)}
              className="bg-amber-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-amber-800 flex-shrink-0"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Vervollständigen
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20"><Loader2 size={32} className="animate-spin mx-auto text-gray-400" /></div>
        ) : !readyCheck ? (
          <WelcomeEmptyState />
        ) : (
          <DashboardContent readyCheck={readyCheck} milestones={milestones} sessions={sessions} load={load} activeSessionId={activeSessionId} setActiveSessionId={setActiveSessionId} />
        )}
      </main>

      <footer className="border-t border-gray-200 mt-16 py-6 px-6 text-center text-[11px] text-gray-400">
        <p>EnBet Consulting · Kundenbereich · <a href="mailto:hallo@enbet-consulting.de" className="text-[#658a07] hover:underline">hallo@enbet-consulting.de</a></p>
      </footer>

      {customer?.must_change_password && (
        <ForceChangePasswordModal onDone={() => setCustomer((c) => ({ ...c, must_change_password: false }))} />
      )}

      {showBillingWizard && (
        <BillingOnboardingModal
          onClose={() => setShowBillingWizard(false)}
          onComplete={() => { setBillingComplete(true); setShowBillingWizard(false); }}
        />
      )}
    </div>
  );
}

function ForceChangePasswordModal({ onDone }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== newPw2) { setError('Die neuen Passwörter stimmen nicht überein.'); return; }
    if (newPw.length < 8) { setError('Mindestens 8 Zeichen.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/portal/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      }, { headers: portalHeaders() });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Passwort ändern fehlgeschlagen');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-[460px] overflow-hidden">
        <div className="bg-[#0a0a0a] text-white px-6 py-4">
          <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Einmal-Passwort
          </p>
          <h3 className="text-lg font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Bitte neues Passwort wählen
          </h3>
          <p className="text-[11px] text-gray-300 mt-2">
            Aus Sicherheitsgründen: das Passwort aus der Welcome-Mail ist ein Einmal-Passwort. Bitte jetzt durch ein eigenes ersetzen.
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-xs">
              <AlertTriangle size={12} className="inline mr-1" /> {error}
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Aktuelles Passwort (aus Welcome-Mail)</label>
            <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required autoComplete="current-password"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Neues Passwort (mind. 8 Zeichen)</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} autoComplete="new-password"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Neues Passwort wiederholen</label>
            <input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} required minLength={8} autoComplete="new-password"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <button type="submit" disabled={busy}
            className="w-full bg-[#0a0a0a] text-[#befa0a] px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {busy ? <><Loader2 size={14} className="animate-spin" /> Speichere…</> : 'Passwort übernehmen'}
          </button>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Dashboard-Content (mit Ready-Check)
// =============================================================================

function DashboardContent({ readyCheck, milestones, sessions, load, activeSessionId, setActiveSessionId }) {
  const unlockedSessions = sessions.filter((s) => s.status !== 'locked').length;
  const totalSessions = sessions.length;
  const approvedSessions = sessions.filter((s) => s.status === 'approved').length;
  const doneMilestones = milestones.filter((m) => m.done_at).length;
  const totalMilestones = milestones.length || 1;

  // Modul-Fortschritt: drei Etappen, gewichtet
  //   1. Termine (Kickoff + Gruppen-Termine) → 30%
  //   2. Sessions approved → 50%
  //   3. Report verfügbar → 20%
  const reportReady = readyCheck?.report_available;
  const moduleProgress = Math.round(
    (doneMilestones / totalMilestones) * 30
    + (totalSessions > 0 ? (approvedSessions / totalSessions) * 50 : 0)
    + (reportReady ? 20 : 0)
  );
  const moduleStep = reportReady
    ? 'Report fertig — Modul abgeschlossen 🎉'
    : approvedSessions === totalSessions && totalSessions > 0
      ? 'Alle Sessions freigegeben — wir bereiten den Report vor.'
      : approvedSessions > 0
        ? `${approvedSessions} von ${totalSessions} Sessions freigegeben.`
        : unlockedSessions > 0
          ? `${unlockedSessions} Session${unlockedSessions === 1 ? '' : 's'} bereit — bitte starten.`
          : 'Erst die Termine erledigen, dann werden Chatbots freigeschaltet.';
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  const openReport = async () => {
    if (reportOpen) { setReportOpen(false); return; }
    if (reportData) { setReportOpen(true); return; }
    setReportLoading(true);
    setReportError('');
    try {
      const { data } = await axios.get(`${API}/portal/readycheck/report`, { headers: portalHeaders() });
      setReportData(data);
      setReportOpen(true);
    } catch (e) {
      setReportError(e.response?.data?.detail || 'Report konnte nicht geladen werden.');
    }
    setReportLoading(false);
  };

  return (
    <div className="space-y-10">
      {/* Hero */}
      <div className="bg-white border border-gray-200 p-6 md:p-8">
        <p className="text-[#658a07] text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Modul 1 · KI Ready Check
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Ihr Diagnose-Prozess
        </h2>
        <p className="text-sm text-gray-600 max-w-[700px] mb-6">
          Wir gehen gemeinsam Ihre Firma durch — Kickoff, Gruppen-Termine und themenbezogene Chatbots.
          Nach jedem Gruppen-Termin schalten wir den passenden Chatbot frei, mit dem Sie (oder ein
          Mitarbeiter Ihrer Wahl) die Detailfragen klären.
        </p>

        {/* Modul-Fortschrittsbalken */}
        <div className="bg-[#0a0a0a] text-white p-5 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Modul-Fortschritt
            </p>
            <p className="text-[#befa0a] text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {moduleProgress}%
            </p>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#befa0a] transition-all" style={{ width: `${moduleProgress}%` }} />
          </div>
          <p className="text-[12px] text-gray-300 mt-3">{moduleStep}</p>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <div className={`py-2 border ${doneMilestones === totalMilestones && totalMilestones > 0 ? 'border-[#befa0a] text-[#befa0a]' : 'border-white/20 text-white/50'}`}>
              <p className="font-bold">Termine</p>
              <p className="text-base mt-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{doneMilestones}/{totalMilestones}</p>
            </div>
            <div className={`py-2 border ${approvedSessions === totalSessions && totalSessions > 0 ? 'border-[#befa0a] text-[#befa0a]' : 'border-white/20 text-white/50'}`}>
              <p className="font-bold">Sessions</p>
              <p className="text-base mt-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{approvedSessions}/{totalSessions}</p>
            </div>
            <div className={`py-2 border ${reportReady ? 'border-[#befa0a] text-[#befa0a]' : 'border-white/20 text-white/50'}`}>
              <p className="font-bold">Report</p>
              <p className="text-base mt-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{reportReady ? '✓' : '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Termine" value={`${milestones.filter((m) => m.done_at).length} / ${milestones.length}`} />
        <Stat label="Sessions freigeschaltet" value={`${unlockedSessions} / ${totalSessions}`} color={unlockedSessions > 0 ? 'text-[#658a07]' : 'text-gray-400'} />
        <Stat label="Status" value={<StatusReadyCheck status={readyCheck.status} />} small />
      </div>

      {/* Termine */}
      <section>
        <h3 className="text-lg md:text-xl font-bold text-[#1f1f1f] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Termine
        </h3>
        {milestones.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Noch keine Termine geplant.</p>
        ) : (
          <ol className="space-y-2">
            {milestones.map((m) => (
              <li key={m.id} className={`border ${m.done_at ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'} p-4 flex items-start gap-3`}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: m.done_at ? '#658a07' : '#e5e5e5', color: m.done_at ? '#fff' : '#666' }}>
                  {m.step_order}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#1f1f1f]">{m.title}</p>
                  {m.description && <p className="text-xs text-gray-600 mt-0.5">{m.description}</p>}
                  <p className="text-[11px] text-gray-400 mt-1 inline-flex items-center gap-3">
                    <span><Calendar size={11} className="inline mr-1" /> {m.scheduled_at ? formatGermanDate(m.scheduled_at) : 'Termin folgt'}</span>
                    {(m.unlocks_domains || []).length > 0 && (
                      <span>Schaltet frei: <strong>{(m.unlocks_domains || []).map((d) => DOMAIN_LABELS[d] || d).join(', ')}</strong></span>
                    )}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {m.done_at ? (
                    <span className="inline-flex items-center gap-1 text-[#658a07] text-xs font-bold uppercase tracking-wider">
                      <CheckCircle size={14} /> Erledigt
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400 text-xs uppercase tracking-wider">
                      <Clock size={14} /> Offen
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Sessions */}
      <section>
        <h3 className="text-lg md:text-xl font-bold text-[#1f1f1f] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Ihre Chatbot-Sessions
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Nach jedem Gruppen-Termin werden die passenden Chatbots freigeschaltet. Sie können selbst chatten oder
          einen Mitarbeiter einladen (folgt in Kürze).
        </p>
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Sessions werden nach den Terminen freigeschaltet.</p>
        ) : (
          <div className="space-y-2">
            {[...sessions]
              .sort((a, b) => DOMAIN_ORDER.indexOf(a.domain) - DOMAIN_ORDER.indexOf(b.domain))
              .map((s) => <SessionCard key={s.id} session={s} onOpen={() => setActiveSessionId(s.id)} />)}
          </div>
        )}
      </section>

      {activeSessionId && (
        <ChatModal sessionId={activeSessionId} onClose={() => { setActiveSessionId(null); load(); }} />
      )}

      {/* Report */}
      {readyCheck.report_available && (
        <section className="bg-[#0a0a0a] text-white p-6 md:p-8">
          <div className="flex items-start gap-4">
            <FileText size={32} className="text-[#befa0a] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Ihr Gutachten
              </p>
              <h3 className="text-xl font-bold uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Ready Check Report
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Sebastian hat den Bericht freigegeben. Klicken Sie unten, um ihn anzusehen.
              </p>
              <button
                onClick={openReport}
                disabled={reportLoading}
                className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-white disabled:opacity-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {reportLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {reportOpen ? 'Report ausblenden' : (reportData ? 'Report anzeigen' : 'Report öffnen')}
              </button>
              {reportError && (
                <p className="text-[11px] text-red-400 mt-2">{reportError}</p>
              )}
            </div>
          </div>

          {reportOpen && reportData && (
            <div className="mt-6 bg-white text-[#1f1f1f] p-6 md:p-8 max-h-[80vh] overflow-y-auto">
              {reportData.report_visuals && (
                <div className="mb-8">
                  <ReportCharts visuals={reportData.report_visuals} />
                </div>
              )}
              <PortalMarkdown md={reportData.report_markdown} />
              {reportData.finished_at && (
                <p className="text-[11px] text-gray-400 mt-6 italic">
                  Fertiggestellt am {formatGermanDate(reportData.finished_at)}
                </p>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// =============================================================================
// Session-Card
// =============================================================================

function SessionCard({ session, onOpen }) {
  const meta = SESSION_STATUS[session.status] || SESSION_STATUS.locked;
  const Icon = meta.icon;
  const expert = DOMAIN_EXPERT[session.domain];
  const isDelegated = session.delegated_to_email && session.status !== 'locked';
  const isClickable = ['unlocked', 'in_progress', 'awaiting_chef_approval', 'approved'].includes(session.status);

  const handleClick = () => { if (isClickable && onOpen) onOpen(); };

  return (
    <div onClick={handleClick}
      className={`border p-4 transition-colors ${session.status === 'locked' ? 'border-gray-200 bg-gray-50/40' : 'border-gray-300 bg-white'} ${isClickable ? 'cursor-pointer hover:border-[#658a07]' : ''}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {expert && (
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
              session.status === 'locked' ? 'bg-gray-200 text-gray-400' : 'bg-[#0a0a0a] text-[#befa0a]'
            }`} style={{ fontFamily: "'Oswald', sans-serif" }}>
              {expert.name[0]}
            </div>
          )}
          <div className="min-w-0">
            <h4 className="font-bold text-[#1f1f1f] uppercase tracking-wider text-sm md:text-base truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {expert ? `${expert.name} · ${DOMAIN_LABELS[session.domain] || session.domain}` : (DOMAIN_LABELS[session.domain] || session.domain)}
            </h4>
            {expert && (
              <p className="text-[10px] text-gray-500 truncate">{expert.role}</p>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded flex-shrink-0 ${meta.cls}`}>
          <Icon size={11} className={meta.icon === Loader2 ? 'animate-spin' : ''} />
          {meta.label}
        </span>
      </div>

      {isDelegated && (
        <p className="text-[11px] text-gray-500 italic mb-2">
          Delegiert an: <strong>{session.delegated_to_name || session.delegated_to_email}</strong>
        </p>
      )}

      {session.message_count > 0 && (
        <p className="text-[11px] text-gray-400">{session.message_count} Nachrichten</p>
      )}

      {session.status === 'unlocked' && (
        <p className="text-xs text-[#658a07] mt-2 font-bold">
          → Klicken zum Starten
        </p>
      )}
      {session.status === 'in_progress' && (
        <p className="text-xs text-amber-700 mt-2">
          → Klicken zum Weitermachen
        </p>
      )}
      {session.status === 'approved' && (
        <p className="text-xs text-emerald-700 mt-2">
          → Klicken um Antworten anzusehen
        </p>
      )}
      {session.status === 'locked' && (
        <p className="text-xs text-gray-400 mt-2">
          Wird nach dem zugehörigen Gruppen-Termin freigeschaltet.
        </p>
      )}
    </div>
  );
}

function StatusReadyCheck({ status }) {
  const labels = {
    pending: 'Vorbereitung',
    intake_in_progress: 'Kickoff in Arbeit',
    intake_done: 'Kickoff abgeschlossen',
    pipeline_running: 'Auswertung läuft',
    draft_ready: 'Bericht in Review',
    reviewed: 'Bericht final',
    delivered: 'Abgeschlossen',
    failed: 'Fehler — bitte Sebastian kontaktieren',
  };
  return <span className="text-sm font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{labels[status] || status}</span>;
}

function WelcomeEmptyState() {
  return (
    <div className="bg-white border border-gray-200 p-8 md:p-12 text-center max-w-[700px] mx-auto">
      <ClipboardCheck size={48} className="mx-auto text-[#658a07] mb-4" />
      <h2 className="text-xl md:text-2xl font-bold text-[#1f1f1f] mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Willkommen
      </h2>
      <p className="text-sm text-gray-600 max-w-[500px] mx-auto mb-4">
        Ihr Diagnose-Prozess wird aktuell vorbereitet. Sobald der Kickoff-Termin steht, sehen Sie hier
        Ihren persönlichen Fahrplan.
      </p>
      <p className="text-xs text-gray-400">
        Bei Fragen: <a href="mailto:hallo@enbet-consulting.de" className="text-[#658a07] hover:underline">hallo@enbet-consulting.de</a>
      </p>
    </div>
  );
}

// =============================================================================
// ChatModal — Domain-Chatbot-Konversation
// =============================================================================

function ChatModal({ sessionId, onClose }) {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [result, setResult] = useState(null);   // {key_findings, raw_text, open_questions, topics_covered}
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  // Approval-State (Phase A)
  const [editingResult, setEditingResult] = useState(false);
  const [draftRawText, setDraftRawText] = useState('');
  const [draftFindings, setDraftFindings] = useState([]);
  const [approving, setApproving] = useState(false);
  const [reinviting, setReinviting] = useState(false);
  const messagesEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portal/sessions/${sessionId}`, { headers: portalHeaders() });
      setSession(data.session);
      setMessages(data.messages || []);
      const r = data.session?.result || null;
      setResult(r);
      if (r) {
        setDraftRawText(r.raw_text || '');
        setDraftFindings(Array.isArray(r.key_findings) ? r.key_findings : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Konnte Session nicht laden');
    }
    setLoading(false);
  }, [sessionId]);

  // Initial-Load + Auto-Start bei unlocked
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portal/sessions/${sessionId}`, { headers: portalHeaders() });
        let sess = data.session;
        let msgs = data.messages || [];
        if (sess.status === 'unlocked') {
          const { data: started } = await axios.post(`${API}/portal/sessions/${sessionId}/start`, {}, { headers: portalHeaders() });
          sess = started.session;
          msgs = started.messages || [];
        }
        setSession(sess);
        setMessages(msgs);
        const r = sess?.result || null;
        setResult(r);
        if (r) {
          setDraftRawText(r.raw_text || '');
          setDraftFindings(Array.isArray(r.key_findings) ? r.key_findings : []);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Konnte Session nicht laden');
      }
      setLoading(false);
    })();
  }, [sessionId]);

  // Auto-Scroll zu neuesten Nachrichten
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true); setError('');
    // Optimistisch User-Message anzeigen
    setMessages((prev) => [...prev, { id: 'tmp-' + Date.now(), sender: 'user', message_text: text, created_at: new Date().toISOString() }]);
    setInput('');
    try {
      const { data } = await axios.post(`${API}/portal/sessions/${sessionId}/messages`, { message: text }, { headers: portalHeaders() });
      // Saubere Antwort vom Server holen (mit echten IDs)
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nachricht konnte nicht gesendet werden');
      await load(); // State neu laden, evtl. wurden User-Message + Reply trotzdem gespeichert
    }
    setSending(false);
  };

  const complete = async () => {
    setCompleting(true); setError('');
    try {
      await axios.post(`${API}/portal/sessions/${sessionId}/complete`, {}, { headers: portalHeaders() });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Konnte Session nicht abschließen');
    }
    setCompleting(false);
  };

  const saveResult = async () => {
    setApproving(true); setError('');
    try {
      const newResult = { ...(result || {}), raw_text: draftRawText, key_findings: draftFindings };
      await axios.patch(`${API}/portal/sessions/${sessionId}/result`, { result: newResult }, { headers: portalHeaders() });
      setResult(newResult);
      setEditingResult(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Konnte Antworten nicht speichern');
    }
    setApproving(false);
  };

  const approveSession = async () => {
    setApproving(true); setError('');
    try {
      // Erst Result speichern falls editiert
      if (editingResult) {
        const newResult = { ...(result || {}), raw_text: draftRawText, key_findings: draftFindings };
        await axios.patch(`${API}/portal/sessions/${sessionId}/result`, { result: newResult }, { headers: portalHeaders() });
        setResult(newResult);
      }
      await axios.post(`${API}/portal/sessions/${sessionId}/approve`, {}, { headers: portalHeaders() });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Freigabe fehlgeschlagen');
    }
    setApproving(false);
  };

  const reinviteEmployee = async () => {
    if (!window.confirm('Mitarbeiter erneut einladen? Die alten Antworten bleiben gespeichert, der Mitarbeiter bekommt einen neuen Magic-Link per Mail.')) return;
    setReinviting(true); setError('');
    try {
      await axios.post(`${API}/portal/sessions/${sessionId}/re-invite`, {}, { headers: portalHeaders() });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Re-Invite fehlgeschlagen');
    }
    setReinviting(false);
  };

  const isApproved = session?.status === 'approved';
  const isLocked = session?.status === 'locked';
  const isAwaitingApproval = session?.status === 'awaiting_chef_approval';
  const canChat = session && ['unlocked', 'in_progress'].includes(session.status);
  const userMessageCount = session ? Math.floor((session.message_count || 0) / 2) : 0;
  const maxMessages = session?.max_user_messages || 30;
  const remaining = Math.max(0, maxMessages - userMessageCount);
  // Soll-Wert: ab ~10 User-Nachrichten ist die Diagnose i.d.R. inhaltlich voll
  // (6 Pflichtthemen + ein bisschen Klärung). Davor: weiter chatten, danach: abschließen.
  const targetMessages = 10;
  const progressPct = Math.min(100, Math.round((userMessageCount / targetMessages) * 100));
  const readyToFinish = userMessageCount >= targetMessages;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-2 md:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[820px] h-[calc(100vh-1rem)] md:h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {session?.domain && DOMAIN_EXPERT[session.domain] && (
              <div className="w-10 h-10 rounded-full bg-[#befa0a] text-[#0a0a0a] flex items-center justify-center text-base font-bold flex-shrink-0" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {DOMAIN_EXPERT[session.domain].name[0]}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {session?.domain && DOMAIN_EXPERT[session.domain]
                  ? `${DOMAIN_EXPERT[session.domain].name} · ${DOMAIN_EXPERT[session.domain].role}`
                  : `Chatbot · ${session?.domain_label || session?.domain || '…'}`}
              </p>
              <h3 className="text-base md:text-lg font-bold uppercase truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {isApproved ? 'Antworten gespeichert' : 'Diagnose-Gespräch'}
              </h3>
              {session && canChat && (
                <div className="mt-2 w-full max-w-[280px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-gray-300">
                      {userMessageCount} / ~{targetMessages} Antworten
                    </span>
                    <span className={`text-[10px] font-bold ${readyToFinish ? 'text-[#befa0a]' : 'text-gray-400'}`}>
                      {readyToFinish ? '✓ Genug Material' : `noch ~${Math.max(0, targetMessages - userMessageCount)}`}
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded overflow-hidden">
                    <div
                      className={`h-full transition-all ${readyToFinish ? 'bg-[#befa0a]' : 'bg-[#658a07]'}`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canChat && (
              <button onClick={() => setShowInvite(true)}
                className="text-[#befa0a] hover:bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 border border-[#befa0a]/40"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                <UserPlus size={12} /> Mitarbeiter einladen
              </button>
            )}
            <button onClick={onClose} className="text-white/60 hover:text-white p-1"><X size={20} /></button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm flex-shrink-0">
            <AlertTriangle size={14} className="inline mr-2" /> {error}
          </div>
        )}

        {/* Chat-Verlauf */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3 bg-[#fafafa]">
          {loading ? (
            <div className="text-center py-12"><Loader2 size={28} className="animate-spin mx-auto text-gray-400" /></div>
          ) : isLocked ? (
            <div className="text-center py-12 text-gray-500">
              <Lock size={28} className="mx-auto mb-2 opacity-50" />
              Diese Session ist noch gesperrt. Wir schalten sie nach dem zugehörigen Gruppen-Termin frei.
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-gray-400 italic">Noch keine Nachrichten.</div>
          ) : (
            messages.map((m) => (
              <ChatBubble key={m.id} message={m} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Eingabe */}
        {canChat && (
          <div className="border-t border-gray-200 bg-white p-3 md:p-4 flex-shrink-0">
            {confirmComplete ? (
              <div className="bg-amber-50 border border-amber-300 p-3 rounded">
                <p className="text-sm text-amber-900 mb-3">
                  Wirklich abschließen? Du kannst danach keine weiteren Nachrichten mehr senden — deine Antworten gehen in den Auswertungs-Report.
                </p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setConfirmComplete(false)}
                    className="text-xs uppercase tracking-wider px-3 py-2 text-gray-500 hover:text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Weiter chatten
                  </button>
                  <button onClick={complete} disabled={completing}
                    className="bg-[#658a07] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#496306] disabled:opacity-50 inline-flex items-center gap-2"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {completing ? <><Loader2 size={12} className="animate-spin" /> Speichere…</> : <><CheckCircle size={14} /> Ja, abschließen</>}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 items-end">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Antwort eingeben… (Enter = senden, Shift+Enter = neue Zeile)"
                  rows={2}
                  disabled={sending}
                  className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none"
                />
                <div className="flex flex-col gap-2">
                  <button onClick={send} disabled={sending || !input.trim()}
                    className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1.5"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /></>}
                  </button>
                  <button onClick={() => setConfirmComplete(true)} disabled={sending || messages.length < 4}
                    title={messages.length < 4 ? 'Erst ein paar Nachrichten austauschen' : (readyToFinish ? 'Du hast genug Material — Session abschließen' : 'Session jetzt abschließen (kannst auch noch weiter chatten)')}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-wider disabled:opacity-40 ${readyToFinish ? 'bg-[#658a07] text-white border border-[#658a07] hover:bg-[#4f6c05] font-bold animate-pulse' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {readyToFinish ? '✓ Fertig' : 'Fertig'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Awaiting-Chef-Approval — Chef sichtet Mitarbeiter-Antworten + gibt frei */}
        {isAwaitingApproval && (
          <div className="border-t border-amber-200 bg-amber-50/50 p-3 md:p-5 flex-shrink-0 overflow-y-auto max-h-[60vh]">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900">Antworten von {session?.delegated_to_name || session?.delegated_to_email || 'Mitarbeiter'} prüfen</p>
                <p className="text-xs text-amber-800">Lies durch was reingekommen ist, korrigiere wenn nötig — und gib frei, was im Report landen soll.</p>
              </div>
            </div>

            {result ? (
              <div className="space-y-3">
                {/* Key-Findings */}
                <div className="bg-white border border-amber-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Wichtigste Erkenntnisse ({(editingResult ? draftFindings : (result.key_findings || [])).length})
                    </p>
                    {!editingResult && (
                      <button onClick={() => setEditingResult(true)}
                        className="text-[10px] uppercase tracking-wider border border-gray-300 px-2 py-1 hover:bg-gray-50"
                        style={{ fontFamily: "'Oswald', sans-serif" }}>
                        Bearbeiten
                      </button>
                    )}
                  </div>
                  {!editingResult ? (
                    (result.key_findings || []).length > 0 ? (
                      <ul className="space-y-1 text-sm">
                        {(result.key_findings || []).map((f, i) => (
                          <li key={i} className="text-gray-800">• {f}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs italic text-gray-400">Keine strukturierten Findings</p>
                    )
                  ) : (
                    <div className="space-y-1.5">
                      {draftFindings.map((f, i) => (
                        <div key={i} className="flex gap-1">
                          <input type="text" value={f} onChange={(e) => setDraftFindings((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                            className="flex-1 border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-[#befa0a]" />
                          <button onClick={() => setDraftFindings((arr) => arr.filter((_, j) => j !== i))}
                            className="text-[10px] text-red-600 hover:text-red-800 px-2">✕</button>
                        </div>
                      ))}
                      <button onClick={() => setDraftFindings((arr) => [...arr, ''])}
                        className="text-[10px] uppercase tracking-wider border border-dashed border-gray-300 px-3 py-1 hover:bg-gray-50 w-full"
                        style={{ fontFamily: "'Oswald', sans-serif" }}>
                        + Finding hinzufügen
                      </button>
                    </div>
                  )}
                </div>

                {/* Zusammenfassung */}
                <div className="bg-white border border-amber-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Zusammenfassung
                  </p>
                  {!editingResult ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.raw_text || '(keine Zusammenfassung)'}</p>
                  ) : (
                    <textarea value={draftRawText} onChange={(e) => setDraftRawText(e.target.value)} rows={4}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  )}
                </div>

                {/* Open Questions wenn vorhanden */}
                {!editingResult && result.open_questions?.length > 0 && (
                  <div className="bg-white border border-gray-200 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Nicht beantwortete Themen ({result.open_questions.length})
                    </p>
                    <ul className="space-y-0.5 text-xs text-gray-600">
                      {result.open_questions.map((q, i) => <li key={i}>· {q}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs italic text-amber-700">Noch keine strukturierte Zusammenfassung — Inhalte siehst du oben im Chat-Verlauf.</p>
            )}

            {/* Action-Buttons */}
            <div className="flex gap-2 mt-4 flex-wrap">
              {editingResult ? (
                <>
                  <button onClick={() => { setEditingResult(false); setDraftRawText(result?.raw_text || ''); setDraftFindings(Array.isArray(result?.key_findings) ? result.key_findings : []); }}
                    className="px-3 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Abbrechen
                  </button>
                  <button onClick={saveResult} disabled={approving}
                    className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-50"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {approving ? 'Speichere…' : 'Speichern'}
                  </button>
                </>
              ) : (
                <button onClick={reinviteEmployee} disabled={reinviting}
                  className="border border-amber-300 px-3 py-2 text-xs font-bold uppercase tracking-wider text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {reinviting ? 'Sende…' : 'Erneut einladen'}
                </button>
              )}
              <button onClick={approveSession} disabled={approving || reinviting}
                className="ml-auto bg-[#658a07] text-white px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#496306] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {approving ? <><Loader2 size={12} className="animate-spin" /> Gebe frei…</> : <><CheckCircle size={14} /> {editingResult ? 'Speichern + Freigeben' : 'Freigeben'}</>}
              </button>
            </div>
          </div>
        )}

        {/* Approved-State (read-only) */}
        {isApproved && (
          <div className="border-t border-gray-200 bg-emerald-50 p-3 md:p-4 flex items-center gap-3 flex-shrink-0">
            <CheckCircle size={20} className="text-emerald-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-800">Antworten gespeichert</p>
              <p className="text-xs text-emerald-700">Diese Session ist abgeschlossen. Die Inhalte fließen in den Auswertungs-Report ein.</p>
            </div>
          </div>
        )}

        {showInvite && (
          <InviteEmployeeSubModal
            sessionId={sessionId}
            domainLabel={session?.domain_label || ''}
            onClose={() => setShowInvite(false)}
          />
        )}
      </div>
    </div>
  );
}

function InviteEmployeeSubModal({ sessionId, domainLabel, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/portal/sessions/${sessionId}/invite-employee`, {
        email: email.trim(), name: name.trim(), send_mail: true,
      }, { headers: portalHeaders() });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Einladung fehlgeschlagen');
    }
    setBusy(false);
  };

  const copyLink = () => {
    if (!result?.magic_url) return;
    navigator.clipboard.writeText(result.magic_url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div className="absolute inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[480px]">
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex items-start justify-between">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Mitarbeiter einladen
            </p>
            <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {domainLabel}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1"><X size={18} /></button>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm">
            <AlertTriangle size={14} className="inline mr-2" /> {error}
          </div>
        )}

        {!result ? (
          <form onSubmit={submit} className="p-5 space-y-4">
            <p className="text-xs text-gray-600">
              Der Mitarbeiter bekommt einen persönlichen Link per E-Mail. Kein Account-Anlegen, kein Passwort.
              Nach Beantwortung gehen die Antworten zur Freigabe an Sie zurück.
            </p>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="mitarbeiter@firma.de"
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Name (optional)</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Vorname Nachname"
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]"
                style={{ fontFamily: "'Oswald', sans-serif" }}>Abbrechen</button>
              <button type="submit" disabled={busy || !email.trim()}
                className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {busy ? <><Loader2 size={12} className="animate-spin" /> Sende…</> : <><Mail size={14} /> Einladung senden</>}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded">
              <p className="text-sm font-bold text-emerald-800 mb-1">
                <CheckCircle size={14} className="inline mr-1" /> Einladung gespeichert
              </p>
              {result.mail_sent ? (
                <p className="text-xs text-emerald-700">Mail wurde an <strong>{email}</strong> versendet.</p>
              ) : (
                <p className="text-xs text-amber-700">⚠ Mail-Versand fehlgeschlagen — bitte Link manuell weitergeben:</p>
              )}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Magic-Link (14 Tage gültig)</label>
              <div className="flex gap-2">
                <input readOnly value={result.magic_url} className="flex-1 border border-gray-300 px-2 py-1.5 text-[11px] font-mono bg-gray-50" />
                <button onClick={copyLink}
                  className="text-[10px] uppercase tracking-wider border border-gray-300 px-2 py-1 hover:border-[#befa0a] font-bold"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {copied ? '✓' : 'Kopieren'}
                </button>
              </div>
            </div>
            <button onClick={onClose}
              className="w-full bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a]"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Schließen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.sender === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] md:max-w-[70%] px-4 py-2.5 rounded-lg ${
        isUser ? 'bg-[#0a0a0a] text-white' : 'bg-white border border-gray-200 text-[#1f1f1f]'
      }`}>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message_text}</p>
      </div>
    </div>
  );
}

function Stat({ label, value, color, small }) {
  return (
    <div className="border border-gray-200 bg-white p-4">
      <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      <p className={`${small ? 'text-base' : 'text-2xl'} font-bold ${color || 'text-[#1f1f1f]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{value}</p>
    </div>
  );
}

// Minimaler Markdown-Renderer für den Portal-Report.
// Bewusst klein gehalten — wir kontrollieren die Markdown-Struktur (kommt aus der Pipeline).
function PortalMarkdown({ md }) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const blocks = [];
  let listBuffer = null;
  let paraBuffer = [];

  const flushPara = () => { if (paraBuffer.length > 0) { blocks.push({ type: 'p', text: paraBuffer.join(' ') }); paraBuffer = []; } };
  const flushList = () => { if (listBuffer) { blocks.push({ type: 'ul', items: listBuffer }); listBuffer = null; } };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h1', text: line.slice(2) }); }
    else if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(3) }); }
    else if (line.startsWith('### ')) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.slice(4) }); }
    else if (line.startsWith('- ') || line.startsWith('* ')) { flushPara(); if (!listBuffer) listBuffer = []; listBuffer.push(line.slice(2)); }
    else { flushList(); paraBuffer.push(line); }
  }
  flushPara(); flushList();

  const inline = (text) => {
    const parts = [];
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0; let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
      const tok = m[0];
      if (tok.startsWith('**')) parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
      else if (tok.startsWith('*')) parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
      else parts.push(<code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-xs">{tok.slice(1, -1)}</code>);
      last = m.index + tok.length;
    }
    if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
    return parts;
  };

  return (
    <div className="leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h1 key={i} className="text-2xl font-bold mt-6 mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h1>;
        if (b.type === 'h2') return <h2 key={i} className="text-xl font-bold mt-5 mb-2 text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h2>;
        if (b.type === 'h3') return <h3 key={i} className="text-base font-bold mt-4 mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h3>;
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-2 space-y-1 text-sm">
            {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
          </ul>
        );
        return <p key={i} className="my-2 text-sm">{inline(b.text)}</p>;
      })}
    </div>
  );
}
